import Component from '@glimmer/component'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { buildDataOperationsConfigProxy } from 'teacher-ui-client/components/data-operations/data-operations-proxy'
import type { IntlService } from 'ember-intl'
import type SessionService from 'teacher-ui-client/services/session'
import { SortDirection, SortType } from 'teacher-ui-client/components/data-operations/table/sort-definition'
import { formatDate, t } from 'ember-intl'
import { on } from '@ember/modifier'
import { fn, array } from '@ember/helper'
import { eq, not } from 'ember-truth-helpers'
import { FidgetLoadingIndicatorComponent } from '@blakeelearning/fidget/components/fidget/loading-indicator'
import { DataOperationsTable } from 'teacher-ui-client/components/data-operations/table'
import { BookLibraryShareStatus } from 'teacher-ui-client/components/book-library/share-status'
import { UiButtonLinkTo } from 'teacher-ui-client/components/ui-button/link-to'
import { NoResultsMessage } from 'teacher-ui-client/components/no-results-message'
import type BookLibrarySharesByStudentShowRoute from 'teacher-ui-client/subscription-type/book-library/shares/index/by-student/show/route'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface Args {
  model: ModelFor<BookLibrarySharesByStudentShowRoute>
}

interface Signature {
  Element: HTMLTableElement
  Args: Args
}

export class BookLibraryStudentSharesTable extends Component<Signature> {
  @service intl!: IntlService

  @service session!: SessionService

  get data() {
    const { shares = [], teachers = [] } = this.args.model.fetchSharesForStudent.last?.value ?? {}
    return (
      shares.filter(hasId).map((share) => {
        const teacher = teachers.find((t) => t.id === share.sharedByUserId)
        let sharedByName = '-'
        if (teacher) {
          sharedByName = teacher.id === this.session.currentTeacher.id ? this.intl.t('me') : teacher.fullName
        }
        return {
          id: share.id,
          collectionName: share.collection.name,
          collectionId: share.collection.id,
          sharedBy: sharedByName,
          record: share,
        }
      }) ?? []
    )
  }

  get dataOperationsConfig() {
    return buildDataOperationsConfigProxy(this)
  }

  @tracked sortCol = 'firstName'

  @tracked sortDir = SortDirection.Asc

  @tracked sortCompare = SortType.ByValue

  @tracked currentPage = 1

  @tracked recordsPerPage = 6

  get columns() {
    return [
      {
        label: this.intl.t('bookLibrary.components.studentSharesTable.headers.collectionName'),
        valuePath: 'collectionName',
        classNames: 'w-2/12',
        sortable: SortType.ByValue,
      },
      {
        label: this.intl.t('bookLibrary.components.studentSharesTable.headers.sharedBy'),
        valuePath: 'sharedBy',
        classNames: 'w-2/12',
        sortable: SortType.ByValue,
      },
      {
        label: this.intl.t('bookLibrary.components.studentSharesTable.headers.startDate'),
        valuePath: 'record.startsAt',
        classNames: 'w-2/12',
        sortable: SortType.ByDate,
      },
      {
        label: this.intl.t('bookLibrary.components.studentSharesTable.headers.endDate'),
        valuePath: 'record.expiresAt',
        classNames: 'w-2/12',
        sortable: SortType.ByDate,
      },
      {
        label: this.intl.t('bookLibrary.components.studentSharesTable.headers.shareStatus'),
        valuePath: 'record.status',
        classNames: 'w-2/12',
        sortable: SortType.ByValue,
      },
      {
        sortable: SortType.ByValue,
        valuePath: '',
      },
    ]
  }

  <template>
    {{#if @model.fetchSharesForStudent.isRunning}}
      <FidgetLoadingIndicatorComponent
        data-test-student-shares-loading
        class="my-2"
        @show={{true}}
        @centered={{true}}
        @appearance="small"
        @overlay={{false}}
      />
    {{else if (not this.data)}}
      <NoResultsMessage @compact={{true}} @body={{t "bookLibrary.components.studentSharesTable.emptyMessage"}} />
    {{else}}
      <DataOperationsTable @dataOperationsConfig={{this.dataOperationsConfig}} as |_selectRecords sorting pagination|>
        <table ...attributes class="mb-2 min-w-full table-fixed bg-white">
          <thead>
            <tr>
              {{#each this.columns as |col|}}
                <th
                  role="button"
                  data-test-col={{col.valuePath}}
                  class="{{col.classNames}} text-dusty-black-250 px-4 py-2 font-normal select-none"
                  {{on "click" (fn sorting.sort col.sortable col.valuePath)}}
                >
                  <span class="text-dusty-black-250 flex items-center space-x-2">
                    <span>{{col.label}}</span>
                    {{#if col.sortable}}
                      <PhosphorIcon
                        data-test-sort-icon
                        @icon={{if
                          (eq sorting.sortCol col.valuePath)
                          (if (eq sorting.sortDir "asc") "phosphor-caret-up-fill" "phosphor-caret-down-fill")
                          "phosphor-caret-up-down-fill"
                        }}
                        role="presentation"
                      />
                    {{/if}}
                  </span>
                </th>
              {{/each}}
            </tr>
          </thead>
          <tbody>
            {{#each pagination.currentPageRecords as |row index|}}
              <tr data-test-record-row={{index}} class="py-3">
                <td class="truncate px-4 py-2" data-test-cell="collectionName">
                  {{row.collectionName}}
                </td>
                <td class="truncate px-4 py-2" data-test-cell="sharedBy">
                  {{row.sharedBy}}
                </td>
                <td class="truncate px-4 py-2" data-test-cell="startsAt">
                  {{formatDate row.record.startsAt weekday="short" day="2-digit" month="short" year="2-digit"}}
                </td>
                <td class="truncate px-4 py-2" data-test-cell="expiresAt">
                  {{formatDate row.record.expiresAt weekday="short" day="2-digit" month="short" year="2-digit"}}
                </td>
                <td class="px-4 py-2 whitespace-nowrap" data-test-cell="status">
                  <BookLibraryShareStatus class="px-2 py-1 text-sm" @share={{row.record}} />
                </td>
                <td class="px-4 py-2 text-right whitespace-nowrap">
                  <UiButtonLinkTo
                    data-test-view-collection-link
                    class="text-sm"
                    @route="subscription-type.book-library.collection.show"
                    @models={{array row.collectionId}}
                  >
                    <PhosphorIcon @icon="phosphor-arrow-square-out-bold" class="text-dusty-black-250 pr-1" />
                    {{t "bookLibrary.components.studentSharesTable.goToCollection"}}
                  </UiButtonLinkTo>
                </td>
              </tr>
            {{/each}}
          </tbody>
        </table>
      </DataOperationsTable>
    {{/if}}
  </template>
}

export default BookLibraryStudentSharesTable
